<template>
  <ts-card>
    <div class="form-header ts-bg-gray-100 ts-border-b ts-border-gray-400 ts-p-4 ts-relative">
      <h4 class="ts-text-sm">{{ $t('payUserVariable.pageTitle') }}</h4>
      <ts-modal-close-button />
    </div>
    <ts-card-wrapper>
      <form>
        <div class="ts-flex ts--mx-2">
          <div class="ts-w-1/3 ts-px-2">
            <div class="form-group m-b-10">
              <label class="col-form-label control-label required">{{ $t('payUserVariable.uvName') }}</label>
              <div>
                <ts-loading-banner :loading="gettingVariableName" fontSize="1rem">
                  <ts-input v-model="model.uv_name" :class="{ 'is-invalid': errors.has('uv_name') }" disabled />
                </ts-loading-banner>
                <div class="invalid-feedback" v-if="errors.has('uv_name')">{{ errors.first('uv_name') }}</div>
              </div>
            </div>
          </div>
          <div class="ts-w-1/3 ts-px-2">
            <div>
              <div class="form-group m-b-10">
                <label class="col-form-label control-label required">{{ $t('payUserVariable.description') }}</label>
                <div>
                  <ts-input
                    v-model="model.description"
                    :class="{ 'is-invalid': errors.has('description') }"
                    autofucus
                  />
                  <div class="invalid-feedback" v-if="errors.has('description')">{{ errors.first('description') }}</div>
                </div>
              </div>
            </div>
          </div>
          <div class="ts-w-1/3 ts-px-2">
            <div>
              <div class="form-group m-b-10">
                <label class="col-form-label control-label required">{{ $t('payUserVariable.dataType') }}</label>
                <div>
                  <ts-select
                    v-model="model.data_type"
                    :options="['Numeric', 'String']"
                    :class="{ 'is-invalid': errors.has('data_type') }"
                  />
                  <div class="invalid-feedback" v-if="errors.has('data_type')">{{ errors.first('data_type') }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="ts-mt-3">
          <div class="alert alert-info fade show ts-flex ts-items-center">
            <i class="fas fa-info-circle ts-text-xl"></i>
            <span class="ml-2">Click on variables to compose a new formula.</span>
          </div>
          <div class="ts-flex ts--mx-3">
            <div class="ts-w-1/2 ts-px-3">
              <system-variables @select-variable="onSelectSystemVariable" />
            </div>
            <div class="ts-w-1/2 ts-px-3">
              <user-variables @select-variable="onSelectSystemVariable" />
            </div>
          </div>
        </div>
        <div class="row ts-mt-4">
          <div class="col-sm-12">
            <ts-button size="sm" class="ts-mr-2" @click="onSignClick('+')">
              <i class="fas fa-plus"></i>
            </ts-button>
            <ts-button size="sm" class="ts-mr-2" @click="onSignClick('-')">
              <i class="fas fa-minus"></i>
            </ts-button>
            <ts-button size="sm" class="ts-mr-2" @click="onSignClick('*')">
              <i class="fas fa-asterisk"></i>
            </ts-button>
            <ts-button size="sm" class="ts-mr-2" @click="onSignClick('/')">
              <i class="fas fa-divide"></i>
            </ts-button>
            <ts-button size="sm" class="ts-mr-2" @click="onSignClick('(')">
              <span class="ts-font-extrabold">(</span>
            </ts-button>
            <ts-button size="sm" class="ts-mr-2" @click="onSignClick(')')">
              <span class="ts-font-extrabold">)</span>
            </ts-button>
          </div>
        </div>
        <div class="row ts-mt-2">
          <div class="col-sm-12">
            <div class="form-group m-b-10">
              <label class="col-form-label control-label required">{{ $t('payUserVariable.formula') }}</label>
              <div>
                <ts-textarea
                  v-model="model.formula"
                  :class="{ 'is-invalid': errors.has('formula') }"
                  ref="txtFormula"
                  rows="3"
                />
                <div class="invalid-feedback" v-if="errors.has('formula')">{{ errors.first('formula') }}</div>
                <div class="valid-feedback" v-if="correctSyntaxMessage">{{ correctSyntaxMessage }}</div>
              </div>
            </div>
          </div>
        </div>
        <div class="row ts-mt-6">
          <div class="col-sm-12 col-lg-12 col-md-12 ts-flex">
            <ts-button
              outline
              color="primary"
              :waiting="checkingSyntax"
              :disabled="model.formula.trim() === ''"
              @click="checkFormulaSyntax"
              >{{ $t('payUserVariable.checkSyntax') }}</ts-button
            >
            <span class="ts-flex-grow"></span>
            <ts-button class="ml-2" @click.prevent="$emit('close')" :disabled="saving"
              >{{ $t('button.cancel') }}
            </ts-button>
            <ts-button
              outline
              color="primary"
              class="ml-2"
              :waiting="savingAndNew"
              @click.prevent="saveAndNew"
              v-if="editData === undefined"
              >{{ $t('button.createAndNew') }}</ts-button
            >
            <ts-button color="primary" class="ml-2" :waiting="saving" @click.prevent="save">
              {{ editData ? $t('button.update') : $t('button.create') }}
            </ts-button>
          </div>
        </div>
      </form>
    </ts-card-wrapper>
  </ts-card>
</template>

<script>
import { Errors } from 'form-backend-validation'
import { cloneDeep } from 'lodash'
import SystemVariables from './system-variables'
import UserVariables from './user-variables'
import UserVariableApi from '@/api/hr/pay-user-variable'

export default {
  name: 'pay-item-overtime-form',
  components: { SystemVariables, UserVariables },
  props: ['editData'],
  data() {
    const model = {
      uv_name: this.editData ? this.editData.uv_name : '',
      description: this.editData ? this.editData.description : '',
      formula: this.editData ? this.editData.formula : '',
      data_type: this.editData ? this.editData.data_type : ''
    }

    return {
      newModel: model,
      model: cloneDeep(model),
      errors: new Errors(),
      saving: false,
      savingAndNew: false,
      checkingSyntax: false,
      correctSyntaxMessage: '',
      gettingVariableName: false
    }
  },
  created() {
    this.getVariableName()
  },
  methods: {
    checkFormulaSyntax() {
      this.checkingSyntax = true
      this.correctSyntaxMessage = ''

      UserVariableApi.checkUserVariableSyntax(this.model.formula)
        .then(response => {
          this.errors.clear('formula')
          this.correctSyntaxMessage = response.message
        })
        .catch(error => {
          this.errors = new Errors({ ...this.errors.all(), formula: error.errors.formula })
        })
        .finally(() => {
          this.checkingSyntax = false
        })
    },
    onSignClick(sign) {
      this.model.formula = `${this.model.formula} ${sign} `
      this.$refs.txtFormula.$el.focus()
    },
    getVariableName() {
      if (this.editData === undefined) {
        this.gettingVariableName = true

        UserVariableApi.getNewUserVariableName().then(response => {
          this.model.uv_name = response.data
          this.gettingVariableName = false
        })
      }
    },
    onSelectSystemVariable(variable) {
      this.model.formula = `${this.model.formula}${variable}`
      this.$refs.txtFormula.$el.focus()
    },
    submit() {
      let responseResult

      this.errors = new Errors()

      if (this.editData) {
        responseResult = this.$store.dispatch('hr/payUserVariable/update', {
          id: this.editData.id,
          ...this.model
        })
      } else {
        responseResult = this.$store.dispatch('hr/payUserVariable/create', this.model)
      }

      return responseResult
        .then(response => {
          return this.$store.dispatch('hr/payUserVariable/fetch').then(res => {
            this.saving = false
            this.$toasted.success(response.message)
            return Promise.resolve(res)
          })
        })
        .catch(error => {
          if (error.errors) this.errors = new Errors(error.errors)
          else this.$toasted.error(error.message)

          return Promise.reject(error)
        })
    },
    save() {
      this.saving = true

      this.submit()
        .then(() => {
          this.$emit('close')
        })
        .finally(() => {
          this.saving = false
        })
    },
    saveAndNew() {
      this.savingAndNew = true

      this.submit()
        .then(() => {
          this.model = cloneDeep(this.newModel)
        })
        .finally(() => {
          this.savingAndNew = false
        })
    }
  }
}
</script>
